/* eslint-disable new-cap */
import React, { useContext } from 'react';
import I18n from 'i18n-js';
import { FetchResult } from '@apollo/client';
import { Redirect } from '@reverbdotcom/commons/src/routing_result';
import { RCButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import {
  core_apimessages_ListingStateUpdate,
} from '@reverbdotcom/commons/src/rql';
import { CheckCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import SellerListingCardContext from '../SellerListingCardContext';
import { ActionType } from '../sellerListingCardReducer';
import { Paths } from '../../shared/url_helpers';
import refetchCardListing from '../refetchCardListing';

interface IMutationError {
  extensions: {
    errors: [{
      message: string
    }]
  },
}

type IExtendedMutationResult<TData> = FetchResult<TData> & {
  errors?: IMutationError[]
};

// Follows statuses used in backend reverb/app/reverb/shops/prepublish_status.rb
export enum PrePublishStatus {
  PAYMENT_POLICY_REQUIRED = 'payment_policy_required',
  SELLER_VERIFICATION_REQUIRED = 'seller_verification_required',
  PROGRESSIVE_ONBOARDING_REQUIRED = 'progressive_onboarding_required',
}

export const COMPONENT_NAME = 'ListingRowPublishButton';

function ListingRowPublishButton({ updateListingState }) {
  const listingCardContext = useContext(
    SellerListingCardContext,
  );

  const { cardState, dispatch } = listingCardContext;

  async function startPublish() {
    dispatch({
      type: ActionType.SET_LOADING_LISTING,
      payload: {
        loadingListing: true,
      },
    });

    try {
      const res = await updateListingState({
        variables: {
          input: {
            productId: cardState.listing.id,
            newState: core_apimessages_ListingStateUpdate.LIVE,
          },
        },
      }) as IExtendedMutationResult<typeof updateListingState>;

      const { hasErrors } = checkResponse(res);

      if (hasErrors) {
        const [userError, nextError] = formatErrors(res.errors);
        if (nextError) {
          handleRedirectErrors(nextError.message);
        } else {
          dispatch({
            type: ActionType.SET_LOADING_LISTING,
            payload: {
              loadingListing: false,
            },
          });
          dispatch({
            type: ActionType.SHOW_TOAST,
            payload: {
              isSuccess: false,
              message: userError?.message,
            },
          });
        }
      } else {
        dispatch({
          type: ActionType.SHOW_TOAST,
          payload: {
            isSuccess: true,
            message: I18n.t(
              'discovery.listingsManagement.sellerListingCard.publishSuccess',
            ),
          },
        });
        
        refetchCardListing(listingCardContext);
      }
    } catch (error) {
      dispatch({
        type: ActionType.SET_LOADING_LISTING,
        payload: {
          loadingListing: false,
        },
      });
      dispatch({
        type: ActionType.SHOW_TOAST,
        payload: {
          isSuccess: false,
          message: I18n.t(
            'discovery.listingsManagement.genericError',
          ),
        },
      });
    }
  }

  function checkResponse(
    res,
  ) {
    return {
      hasErrors: !!res.errors?.length,
      hasData: !!res.data,
    };
  }

  function formatErrors(errors) {
    return errors[0]?.extensions?.errors || [];
  }

  function handleRedirectErrors(errorMessage: string) {
    let url: string;
    switch (errorMessage) {
      case PrePublishStatus.PAYMENT_POLICY_REQUIRED:
        url = Paths.getPaid.expand({
          listingId: cardState.listing.id,
          query: { react_sell_form_publish: true },
        });
        break;
      case PrePublishStatus.SELLER_VERIFICATION_REQUIRED:
        url = Paths.sellerVerificationOnboarding.expand({
          query: {
            listing_id: cardState.listing.id,
            react_sell_form_publish: true,
          },
        });
        break;
      case PrePublishStatus.PROGRESSIVE_ONBOARDING_REQUIRED:
        url = Paths.shopOnboardingSetup.expand({
          query: {
            listing_id: cardState.listing.id,
            react_sell_form_publish: true,
          },
        });
        break;
      default:
        // If it's not an error related to the prepublish status, redirect to the SellFormPostPublish to display error messages
        url = Paths.listingSubmit.expand({
          query: {
            id: cardState.listing.id,
            react_sell_form_publish: true,
          },
        });
    }
    Redirect({ url, fullPageReload: true });
  }

  function handleClick() {
    startPublish();
    trackEvent({
      eventName: MParticleEventName.ClickedComponent,
      componentName: COMPONENT_NAME,
      listingId: cardState.listing.id,
    });
  }

  return (
    <>
      <RCButton
        onClick={handleClick}
        variant="primary"
        disabled={!cardState.listing.publishValidation?.isValid}
        fullWidth
      >
        <div className="tablet-d-none">
          <RCTextWithIcon svgComponent={CheckCircleIcon} placement="right">
            {I18n.t('discovery.listingsManagement.sellerListingCard.publishListing')}
          </RCTextWithIcon>
        </div>
        <div className="seller-listing-card-footer__tablet-button-container">
          <RCTextWithIcon svgComponent={CheckCircleIcon} placement="right">
            {I18n.t('discovery.listingsManagement.sellerListingCard.publish')}
          </RCTextWithIcon>
        </div>
      </RCButton>
    </>
  );
}

export default ListingRowPublishButton;
